import React from "react";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import DatePickerx from "features/datepicker/date-picker";
import SensitiveNumberInput from "components/sensitive-amount/SensitiveNumberInput";
import { FunnelField } from "./types";
import useIsTouchScreen from "./use-is-touch-screen";

interface FunnelStepRendererProps {
  fields: FunnelField[];
  data: Record<string, any>;
  onChange: (field: string, value: any) => void;
  errors: Record<string, string>;
  lang: "lt" | "en";
}

function isFieldVisible(
  field: FunnelField,
  data: Record<string, any>
): boolean {
  if (!field.visibleWhen) return true;
  const { field: depField, operator, value } = field.visibleWhen;
  const depValue = data[depField];

  switch (operator) {
    case "eq":
      return depValue === value;
    case "neq":
      return depValue !== value;
    case "in":
      return Array.isArray(value) && value.includes(depValue);
    case "exists":
      return depValue != null && depValue !== "" && depValue !== false;
    default:
      return true;
  }
}

function getLabel(field: FunnelField, lang: "lt" | "en"): string {
  return field.label[lang] || field.label.en;
}

function getPlaceholder(
  field: FunnelField,
  lang: "lt" | "en"
): string | undefined {
  if (!field.placeholder) return undefined;
  return field.placeholder[lang] || field.placeholder.en;
}

function getHelpText(
  field: FunnelField,
  lang: "lt" | "en"
): string | undefined {
  if (!field.helpText) return undefined;
  return field.helpText[lang] || field.helpText.en;
}

const inputBaseClass =
  "w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-500";

// On a touch screen a field is 16 px text (iOS zooms into smaller ones) and 44 px tall.
function inputClass(isTouch: boolean, hasError: boolean): string {
  return clsx(
    inputBaseClass,
    isTouch ? "min-h-11 text-base" : "text-sm",
    hasError ? "border-red-500" : "border-gray-300"
  );
}

const FIELD_INPUT_TYPE: Partial<Record<FunnelField["type"], string>> = {
  email: "email",
  phone: "tel",
};

const FunnelStepRenderer: React.FC<FunnelStepRendererProps> = ({
  fields,
  data,
  onChange,
  errors,
  lang,
}) => {
  const { t } = useTranslation();
  const isTouch = useIsTouchScreen();
  // Help text sits under the label text, to the right of the checkbox.
  const checkboxIndent = isTouch ? "ml-7" : "ml-6";

  const renderField = (field: FunnelField) => {
    if (!isFieldVisible(field, data)) return null;

    const label = getLabel(field, lang);
    const placeholder = getPlaceholder(field, lang);
    const helpText = getHelpText(field, lang);
    const error = errors[field.name] ? t(errors[field.name]) : undefined;
    const value = data[field.name];

    switch (field.type) {
      case "text":
      case "email":
      case "phone":
        return (
          <div key={field.name}>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {label}
              {!!field.required && <span className="text-red-500 ml-0.5">*</span>}
            </label>
            <input
              className={inputClass(isTouch, !!error)}
              value={value || ""}
              onChange={(e) => onChange(field.name, e.target.value)}
              autoComplete={field.type === "email" ? "email" : "off"}
              placeholder={placeholder}
              disabled={field.readOnly}
              type={FIELD_INPUT_TYPE[field.type] ?? "text"}
              inputMode={field.inputMode}
            />
            {helpText && <p className="text-xs text-gray-500 mt-1">{helpText}</p>}
            {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
          </div>
        );

      case "number": {
        const numberProps: React.ComponentProps<typeof SensitiveNumberInput> = {
          className: inputClass(isTouch, !!error),
          value: value != null ? String(value) : "",
          // The decimal keypad of a phone in Lithuanian types a comma.
          inputMode: "decimal",
          onChange: (e) => {
            const v = e.target.value.replace(",", ".");
            if (v === "" || /^\d*(\.\d*)?$/.test(v))
              onChange(field.name, v === "" ? null : v);
          },
          onBlur: () => {
            if (typeof value === "string" && value !== "") {
              const num = Number(value);
              if (!isNaN(num)) onChange(field.name, num);
            }
          },
          autoComplete: "off",
          placeholder,
          disabled: field.readOnly,
        };
        return (
          <div key={field.name}>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {label}
              {!!field.required && <span className="text-red-500 ml-0.5">*</span>}
            </label>
            {field.sensitive ? <SensitiveNumberInput {...numberProps} /> : <input {...numberProps} />}
            {helpText && <p className="text-xs text-gray-500 mt-1">{helpText}</p>}
            {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
          </div>
        );
      }

      case "textarea":
        return (
          <div key={field.name}>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {label}
              {!!field.required && <span className="text-red-500 ml-0.5">*</span>}
            </label>
            <textarea
              className={inputClass(isTouch, !!error)}
              value={value || ""}
              onChange={(e) => onChange(field.name, e.target.value)}
              rows={4}
              autoComplete="off"
              placeholder={placeholder}
              disabled={field.readOnly}
            />
            {helpText && <p className="text-xs text-gray-500 mt-1">{helpText}</p>}
            {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
          </div>
        );

      case "date":
        return (
          <DatePickerx
            key={field.name}
            title={label}
            selected={value ? formatDateValue(value) : ""}
            onChange={(date: Date | null) => {
              onChange(field.name, date ? date.getTime() : null);
            }}
            isRequired={!!field.required}
            // Checked on "Next" like every other field here, not flagged empty before anything is typed.
            error={error ?? ""}
            disableDatesAfter={null}
            readOnly={field.readOnly}
          />
        );

      case "select":
        return (
          <div key={field.name}>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {label}
              {!!field.required && <span className="text-red-500 ml-0.5">*</span>}
            </label>
            <select
              className={inputClass(isTouch, !!error)}
              value={value || ""}
              onChange={(e) => onChange(field.name, e.target.value)}
              disabled={field.readOnly}
            >
              <option value="">-</option>
              {(field.options || []).map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label[lang] || o.label.en}
                </option>
              ))}
            </select>
            {helpText && <p className="text-xs text-gray-500 mt-1">{helpText}</p>}
            {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
          </div>
        );

      case "radio":
        return (
          <div key={field.name}>
            <p className="text-sm font-semibold">
              {label}
              {field.required && " *"}
            </p>
            <div className="flex flex-col gap-2">
              {(field.options || []).map((o) => (
                <label
                  key={o.value}
                  className={clsx("flex items-center gap-2 cursor-pointer", isTouch && "min-h-11")}
                >
                  <input
                    type="radio"
                    className={isTouch ? "h-5 w-5" : undefined}
                    name={field.name}
                    value={o.value}
                    checked={value === o.value}
                    onChange={() => onChange(field.name, o.value)}
                    disabled={field.readOnly}
                  />
                  {o.label[lang] || o.label.en}
                </label>
              ))}
            </div>
            {error && (
              <p className="text-sm text-red-600">{error}</p>
            )}
          </div>
        );

      case "checkbox":
      case "toggle":
        return (
          <div key={field.name}>
            <label className={clsx("flex items-center gap-2 cursor-pointer", isTouch && "min-h-11")}>
              <input
                type="checkbox"
                className={clsx(
                  isTouch ? "h-5 w-5" : "h-4 w-4",
                  "rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                )}
                checked={!!value}
                onChange={(e) => onChange(field.name, e.target.checked)}
                disabled={field.readOnly}
              />
              <span className="text-sm text-gray-700">{label}</span>
            </label>
            {helpText && <p className={clsx("text-xs text-gray-500 mt-1", checkboxIndent)}>{helpText}</p>}
            {error && <p className={clsx("text-xs text-red-600 mt-1", checkboxIndent)}>{error}</p>}
          </div>
        );

      case "repeater":
        return (
          <RepeaterField
            key={field.name}
            field={field}
            data={data}
            onChange={onChange}
            errors={errors}
            lang={lang}
          />
        );

      case "custom": {
        const Component = field.component;
        if (!Component) return null;
        return (
          <div key={field.name}>
            <Component
              name={field.name}
              value={value}
              onChange={(next) => onChange(field.name, next)}
              label={label}
              required={!!field.required}
              error={error}
              readOnly={field.readOnly}
              data={data}
            />
            {helpText && <p className="text-xs text-gray-500 mt-1">{helpText}</p>}
          </div>
        );
      }

      default:
        return null;
    }
  };

  return <div className="space-y-4">{fields.map(renderField)}</div>;
};

// --- Repeater sub-component ---
interface RepeaterFieldProps {
  field: FunnelField;
  data: Record<string, any>;
  onChange: (field: string, value: any) => void;
  errors: Record<string, string>;
  lang: "lt" | "en";
}

const RepeaterField: React.FC<RepeaterFieldProps> = ({
  field,
  data,
  onChange,
  errors,
  lang,
}) => {
  const { t } = useTranslation();
  const isTouch = useIsTouchScreen();
  if (!isFieldVisible(field, data)) return null;
  const items: any[] = data[field.name] || [];
  const subFields = field.repeaterFields || [];

  const handleItemChange = (index: number, subField: string, value: any) => {
    const updated = [...items];
    updated[index] = { ...updated[index], [subField]: value };
    onChange(field.name, updated);
  };

  const handleAdd = () => {
    const empty: Record<string, any> = {};
    subFields.forEach((sf) => {
      empty[sf.name] = "";
    });
    onChange(field.name, [...items, empty]);
  };

  const handleRemove = (index: number) => {
    onChange(
      field.name,
      items.filter((_, i) => i !== index)
    );
  };

  return (
    <div>
      <p className="text-sm font-semibold">
        {field.label[lang] || field.label.en}
      </p>
      <div className="flex flex-col gap-4">
        {items.map((item, index) => (
          <div
            key={index}
            className="p-3 border border-gray-200 rounded-lg"
          >
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold">#{index + 1}</span>
              <button
                type="button"
                aria-label={t("delete")}
                className={clsx("text-red-600 hover:text-red-700 text-sm", isTouch && "inline-flex h-11 w-11 items-center justify-center")}
                onClick={() => handleRemove(index)}
              >
                ✕
              </button>
            </div>
            <div className="space-y-4">
              {subFields.map((sf) => {
                const sfError = errors[`${field.name}.${index}.${sf.name}`]
                  ? t(errors[`${field.name}.${index}.${sf.name}`])
                  : undefined;
                return (
                  <div key={sf.name}>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {sf.label[lang] || sf.label.en}
                      {!!sf.required && <span className="text-red-500 ml-0.5">*</span>}
                    </label>
                    <input
                      className={inputClass(isTouch, !!sfError)}
                      value={item[sf.name] || ""}
                      onChange={(e) => handleItemChange(index, sf.name, e.target.value)}
                      autoComplete="off"
                      inputMode={sf.inputMode}
                    />
                    {sfError && <p className="text-xs text-red-600 mt-1">{sfError}</p>}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
        <button
          type="button"
          className={clsx(
            "px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-medium",
            isTouch && "min-h-11"
          )}
          onClick={handleAdd}
        >
          + {field.label[lang] || field.label.en}
        </button>
      </div>
    </div>
  );
};

// Helper to format dates (timestamps or ISO strings)
function formatDateValue(value: any): string {
  if (!value) return "";
  if (typeof value === "string" && value.includes("-")) return value.slice(0, 10);
  if (typeof value === "number") {
    return new Date(value).toISOString().split("T")[0];
  }
  return String(value);
}

export default FunnelStepRenderer;
